"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { api, realtime } from '@/services/api';
import type { PinAuthRequest } from '@/types';

interface AuthContextType {
  isAuthenticated: boolean;
  role: string | null;
  token: string | null;
  isLoading: boolean;
  login: (req: PinAuthRequest) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  // 追加: 既存トークンがあれば初期マウント時にWS接続
  useEffect(() => {
    try {
      if (api.getToken && api.getToken()) {
        realtime.connect()
      }
    } catch (_) {}
  }, [])

  useEffect(() => {
    const storedToken = api.getToken();
    const storedRole = api.getRole();
    if (storedToken && storedRole) {
      setToken(storedToken);
      setRole(storedRole);
      // realtime.connect(); // 削除
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (req: PinAuthRequest) => {
    const res = await api.login(req);
    if (res.success && res.data) {
      setToken(res.data.token);
      setRole(res.data.role);
      realtime.connect();
      return true;
    } else {
      console.error('Login failed:', res.error);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    api.logout();
    realtime.disconnect();
    setToken(null);
    setRole(null);
    router.push('/');
  }, [router]);

  const value = {
    isAuthenticated: !!token,
    token,
    role,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
